import { FeaturePrivilegeKey } from '../types';
import {recordActivity} from '../services/accountActivity';
import { usePersonalSettings } from '../services/personalSettings';
import React, { useState, useContext, createContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Trees,
  Settings,
  Menu,
  X,
  Wrench,
  Truck,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Camera,
  LogIn,
  LogOut,
  User2,
  Map,
  Award,
  Route as RouteIcon,
} from 'lucide-react';
import { useModalFocus } from '../services/useModalFocus';
import lakeEmblem from '../assets/brand/sanctuary-emblem.png';
import SidebarBrand from './SidebarBrand';
import '@fontsource-variable/inter';
import sidebarLandscape from '../assets/brand/sidebar-lake-footer.png';
import {usePresentationHold} from '../services/usePresentation';
import WorkspaceSyncStatus from './WorkspaceSyncStatus';
import {RunningProjectWork} from './ProjectWork';
import {RoutePhotoSync} from './RoutePhotoRecovery';
import PushNavigation from './PushNavigation';
import AppInstallPrompt from './AppInstallPrompt';
import AppUpdateNotice from './AppUpdateNotice';
import { SyncService } from '../services/syncService';
import { useAuth } from '../services/authContext';
import { useFarm } from '../services/farmContext';
import { useGuest } from '../services/GuestContext';
import {useCameraBrowsingAccess} from '../services/cameraApi';
import { useUnauthBackground } from '../services/unauthBackgrounds';
import { ServerRateLimitStatus, subscribeToServerRateLimitStatus } from '../services/serverRepository';

// Context Definition
interface LayoutContextType {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LayoutContext = createContext<LayoutContextType>({
  isCollapsed: false,
  setIsCollapsed: () => {},
  isSidebarOpen: false,
  setIsSidebarOpen: () => {},
});

export const useLayout = () => useContext(LayoutContext);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, idToken, privileges, signIn, signOut, loading, error, googleClientReady } = useAuth();
  const { activeFarm } = useFarm();
  const { farm: guestFarm } = useGuest();
  const canViewCameras=useCameraBrowsingAccess(guestFarm?.farm_id);
  const { backgroundUrl } = useUnauthBackground(!user);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [collapsePreference, setCollapsePreference] = useState({key:'',value:false});
  const storageKey = `sidebar-collapsed-${user?.email ?? 'anon'}`;
  const isCollapsed = collapsePreference.key === storageKey && collapsePreference.value;
  const setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>> = (next) => {
    const value = typeof next === 'function' ? next(isCollapsed) : next;
    setCollapsePreference({key:storageKey,value});
    try { localStorage.setItem(storageKey,value?'1':'0'); } catch { /* Storage may be unavailable. */ }
  };
  const [isDesktop,setIsDesktop]=useState(()=>window.matchMedia('(min-width: 1024px)').matches);
  const compact = isDesktop && isCollapsed;
  const sidebarRef=useRef<HTMLElement>(null);
  const brandHeaderRef=useRef<HTMLDivElement>(null);
  const [sharedHeaderScroll,setSharedHeaderScroll]=useState(false);
  useModalFocus(sidebarRef,!!user && isSidebarOpen && !isDesktop,()=>setIsSidebarOpen(false));
  useEffect(()=>{
    const media=window.matchMedia('(min-width: 1024px)');
    const change=()=>{setIsDesktop(media.matches);setIsSidebarOpen(false);};
    media.addEventListener('change',change);
    return ()=>media.removeEventListener('change',change);
  },[]);
  useEffect(()=>{setIsSidebarOpen(false);setIsUserMenuOpen(false);},[location.pathname,user?.email]);
  const {settings:personalSettings}=usePersonalSettings(user?.email,user?.roles??(user?.role?[user.role]:[]));
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  usePresentationHold(!!user && isUserMenuOpen,'Back to the map');
  const [serverRateLimit, setServerRateLimit] = useState<ServerRateLimitStatus>({
    isBusy: false,
    retryingRequests: 0,
    nextAllowedAt: null,
    domain: null,
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  const brandLabel = user ? activeFarm?.name : undefined;
  const accountName = user?.name || personalSettings.name || user?.email || 'Guest';
  const accountInitials = accountName.split(/\s+/).filter(Boolean).slice(0,2).map(part=>part[0]).join('').toUpperCase();
  useEffect(()=>{
    const sidebar=sidebarRef.current,header=brandHeaderRef.current;
    if(!sidebar||!header)return;
    const footer=sidebar.querySelector('footer'),landscape=sidebar.querySelector<HTMLElement>('.sidebar-landscape');
    const measure=()=>setSharedHeaderScroll(header.offsetHeight+176+(footer?.offsetHeight||0)+(landscape?.offsetHeight||0)>sidebar.clientHeight);
    measure();
    const observer=typeof ResizeObserver!=='undefined'?new ResizeObserver(measure):null;
    observer?.observe(sidebar);observer?.observe(header);
    if(footer)observer?.observe(footer);
    if(landscape)observer?.observe(landscape);
    window.addEventListener('resize',measure);
    return()=>{observer?.disconnect();window.removeEventListener('resize',measure);};
  },[brandLabel,compact,user?.email]);

  useEffect(() => {
    const handleOnline = () => {
      if (navigator.onLine) {
        SyncService.requestSync('online');
      }
    };

    const handleFocus = () => { if(navigator.onLine) void SyncService.requestSync('foreground'); };
    const handleVisibility = () => { if(document.visibilityState==='visible') handleFocus(); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus',handleFocus);
    document.addEventListener('visibilitychange',handleVisibility);

    if (navigator.onLine) {
      SyncService.requestSync('startup');
    }

    const timer = setInterval(() => {
      if (navigator.onLine && document.visibilityState==='visible') {
        SyncService.requestSync('interval');
      }
    }, 15000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus',handleFocus);
      document.removeEventListener('visibilitychange',handleVisibility);
      clearInterval(timer);
    };
  }, [user?.email, idToken]);

  useEffect(() => {
    let value=false;
    try { value=localStorage.getItem(storageKey)==='1'; } catch { /* Use expanded navigation. */ }
    setCollapsePreference({key:storageKey,value});
  },[storageKey]);

  useEffect(() => {
    const unsubscribe = subscribeToServerRateLimitStatus(setServerRateLimit);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if we are on the map page to remove padding
  const isMapPage = location.pathname === '/';

  const navGroups = ([
    {
      title: 'Operations',
      items: [
        { label: 'Field Map', path: '/', icon: Map, key: 'map' },
        { label: 'Projects', path: '/projects', icon: Wrench, key: 'projects' },
      ],
    },
    {
      title: 'Community', items: [
        { label: 'Nature Gallery', path: '/community/photos', icon: Camera, key: 'communityParticipation' },
        { label: 'Routes', path: '/routes', icon: RouteIcon, key: 'gpsTracking' },
        { label: 'Patches', path: '/community/badges', icon: Award, key: 'community' },
      ],
    },
    {
      title: 'Management',
      items: [
        { label: 'Assets', path: '/assets', icon: Truck, key: 'assets' },
        { label: 'Places', path: '/map-features', icon: Map, key: 'places' },
        { label: 'Cameras', path: '/cameras', icon: Camera, key: 'cameraAccess' },
        { label: 'Documents', path: '/documents', icon: FolderOpen, key: 'documents' },
      ],
    },
    {
      title: 'Admin',
      items: [
        { label: 'Settings', path: privileges.settings?.view && (user?.roles || [user?.role]).includes('Admin') ? '/settings' : '/settings/moderation', icon: Settings, key: 'settings' },
      ],
    },
  ] as {title:string;items:{label:string;path:string;icon:typeof Trees;key:FeaturePrivilegeKey|'community'|'cameraAccess'}[]}[])
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.key==='cameraAccess' ? canViewCameras : item.key==='community' ? user?.status==='Active' && !!guestFarm : (privileges[item.key]?.view || item.key === 'settings' && (privileges.contributionReview?.view || privileges.photoModeration?.view))),
    }))
    .filter((group) => group.items.length > 0);

  const renderNavItem = (item: { label: string; path: string; icon: typeof Trees; key: FeaturePrivilegeKey|'community'|'cameraAccess' }) => {
    const Icon = item.icon;
    const canView = item.key==='cameraAccess' ? canViewCameras : item.key==='community' ? user?.status==='Active' && !!guestFarm : (privileges[item.key]?.view || item.key === 'settings' && (privileges.contributionReview?.view || privileges.photoModeration?.view));
    if (!canView) return null;
    const isActive =
      location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path+'/'));

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setIsSidebarOpen(false)}
        className={`sidebar-nav-link${compact ? ' sidebar-nav-link--compact' : ''}`}
        aria-current={isActive ? 'page' : undefined}
        aria-label={item.label}
        title={compact ? item.label : undefined}
      >
        <Icon className="sidebar-nav-icon" size={20} strokeWidth={1.75} aria-hidden="true" />
        {!compact && <span className="sidebar-nav-label">{item.label}</span>}
      </Link>
    );
  };

  return (
    <LayoutContext.Provider value={{ isCollapsed, setIsCollapsed, isSidebarOpen, setIsSidebarOpen }}>
      <RoutePhotoSync/><PushNavigation/>
      <AppInstallPrompt/>
      <div
        className="app-shell app-ui flex font-sans overflow-hidden"
        style={
          backgroundUrl
            ? {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : undefined
        }
      >
        <a href="#main-content" className="skip-link">Skip to content</a>
        {/* Mobile Sidebar Overlay */}
        {user && isSidebarOpen && (
          <div
            className="absolute inset-0 z-[1090] bg-black bg-opacity-50 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        {user && <aside
          onClickCapture={event=>{const link=(event.target as Element).closest('a[href]');if(link)recordActivity({kind:'sidebar.clicked',path:new URL(link.getAttribute('href')!,window.location.href).pathname},user?.email);}}
          onAuxClickCapture={event=>{if(event.button!==1)return;const link=(event.target as Element).closest('a[href]');if(link)recordActivity({kind:'sidebar.clicked',path:new URL(link.getAttribute('href')!,window.location.href).pathname},user?.email);}}
          ref={sidebarRef}
          id="app-navigation"
          aria-label="Main navigation"
          role={!isDesktop && isSidebarOpen ? 'dialog' : undefined}
          aria-modal={!isDesktop && isSidebarOpen ? true : undefined}
          tabIndex={!isDesktop ? -1 : undefined}
          data-autofocus={!isDesktop ? '' : undefined}
          {...(!isDesktop && !isSidebarOpen ? {inert:''} : {})}
          className={`
          sf-sidebar absolute inset-y-0 left-0 z-[1100] flex flex-col
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:relative lg:translate-x-0
          ${compact ? 'sf-sidebar--compact' : ''} shrink-0
        `}
        >
          <div className={`sidebar-scroll-region${sharedHeaderScroll ? ' sidebar-scroll-region--shared' : ''}`}>
          {/* Header */}
          <div ref={brandHeaderRef} className={`sidebar-brand-header${compact ? ' sidebar-brand-header--compact' : ''}`}>
            <SidebarBrand name={brandLabel} compact={compact} />
            <button onClick={() => setIsSidebarOpen(false)} aria-label="Close navigation" className="sidebar-control absolute right-1 top-1 lg:hidden h-11 w-11 flex items-center justify-center rounded-lg">
              <X size={24} />
            </button>
          </div>

          {/* Scrollable Nav Area */}
          <nav aria-label="Workspace" className="sidebar-nav custom-scrollbar">
            {navGroups.map((group, index) => (
              <section key={group.title} aria-label={group.title} className="sidebar-nav-group">
                {/* Group Header (Only visible when expanded) */}
                {!compact && (
                  <h2 className="sidebar-section-label">
                    {group.title}
                  </h2>
                )}

                {/* Divider for collapsed mode */}

                {/* Items */}
                <div className="sidebar-nav-rows">{group.items.map(renderNavItem)}</div>
              </section>
            ))}
          </nav>
          </div>
          {!compact && <div className="sidebar-landscape" aria-hidden="true"><img src={sidebarLandscape} alt="" draggable={false}/></div>}

          {/* Footer: Collapse control and user menu */}
          <footer
            className={`sidebar-account${compact ? ' sidebar-account--compact' : ''}`}
          >
            <div
              className={`flex ${compact ? 'flex-col items-center justify-center' : 'items-center justify-between'} gap-2 flex-nowrap`}
            >
              <div className="relative min-w-0" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  className={`sidebar-account-button${compact ? ' sidebar-account-button--compact' : ''}`}
                  aria-expanded={isUserMenuOpen}
                  aria-controls="account-options"
                  onKeyDown={e=>{if(e.key==='Escape')setIsUserMenuOpen(false);}}
                  aria-label="Account menu"
                  title={compact ? 'Account' : undefined}
                >
                  {(user?.picture || personalSettings.picture) ? (
                    <img src={user?.picture || personalSettings.picture} alt="" className="sidebar-avatar" />
                  ) : (
                    <span className="sidebar-avatar sidebar-avatar-initials" aria-hidden="true">{accountInitials}</span>
                  )}
                  {!compact && (
                    <span className="sidebar-account-name">{accountName}</span>
                  )}
                </button>
                {isUserMenuOpen && (
                  <div id="account-options" onKeyDown={e=>{if(e.key==='Escape'){setIsUserMenuOpen(false);userMenuRef.current?.querySelector('button')?.focus();}}} className="absolute left-0 bottom-12 w-60 rounded-xl bg-white text-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[1200]">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold truncate">{user?.name || personalSettings.name || user?.email || 'Guest'}</p>
                      {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-gray-700"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          setIsSidebarOpen(false);
                        }}
                      >
                        <User2 className="h-4 w-4 mr-3 text-gray-500" />
                        Profile & preferences
                      </Link>
                      {user && <Link to="/home" className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-gray-700" onClick={()=>{setIsUserMenuOpen(false);setIsSidebarOpen(false);}}><Map className="h-4 w-4 mr-3 text-gray-500"/>Property guide</Link>}
                      {user ? (
                        <>
                          <button
                            onClick={() => {
                              signOut();
                              setIsUserMenuOpen(false);
                            }}
                            className="sidebar-account-secondary w-full text-left flex items-center px-4 py-2 hover:bg-gray-100 text-gray-700"
                          >
                            <LogOut className="h-4 w-4 mr-3 text-gray-500" />
                            Sign out
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            signIn();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left flex items-center px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 disabled:opacity-50"
                          disabled={loading}
                        >
                          <LogIn className="h-4 w-4 mr-3 text-gray-500" />
                          {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                      )}
                      {error && <div className="px-4 py-2 text-xs text-red-600">{error}</div>}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="sidebar-control hidden lg:inline-flex items-center justify-center h-11 w-11 rounded-md flex-shrink-0"
                aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
                title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {compact ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
              </button>
            </div>
          </footer>
        </aside>}

        {/* Main Content */}
        <div {...(user && !isDesktop && isSidebarOpen ? {inert:''} : {})} className="relative z-[1] min-w-0 min-h-0 flex-1 flex flex-col overflow-hidden">
          {user && <header className="sidebar-mobile-bar lg:hidden flex-shrink-0 z-[1050] relative">
            <div className="px-4 py-3 flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open navigation"
                aria-expanded={isSidebarOpen}
                aria-controls="app-navigation"
                className="sidebar-control h-11 w-11 shrink-0 -ml-2 flex items-center justify-center rounded-lg"
              >
                <Menu size={24} />
              </button>
              <Link to="/" aria-label={brandLabel ? `${brandLabel} — Open the field map` : 'Open the field map'} className="flex min-w-0 items-center gap-3">
                <span className="sidebar-emblem"><img src={lakeEmblem} alt="" aria-hidden="true" width={256} height={256} /></span>
                {brandLabel && <span className="sidebar-property-name">{brandLabel}</span>}
              </Link>
            </div>
          </header>}

          <AppUpdateNotice/>
          {user && <WorkspaceSyncStatus />}
          {user && privileges.projects?.view && <RunningProjectWork />}
          {serverRateLimit.isBusy && (
            <div className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" role="status">
              Server is busy and requests are being retried. Please wait before repeating actions.
            </div>
          )}

          <main id="main-content" tabIndex={-1} className={`workspace-content min-h-0 flex-1 ${isMapPage ? 'workspace-content--map overflow-hidden p-0' : 'workspace-content--padded overflow-y-auto'}`}>
            {isMapPage ? children : <div className="mx-auto w-full max-w-[1440px]">{children}</div>}
          </main>
        </div>
      </div>
    </LayoutContext.Provider>
  );
};

export default Layout;
